/**
 * Team Management API service
 * Handles team invitations and member management
 */

import api from './api';
import type {
  TeamInvitation,
  TeamMember,
  CreateInvitationData,
  UpdateMemberRoleData,
  InvitationResponse,
  AcceptInvitationResponse,
  RevokeInvitationResponse,
} from '../types/team';

export const teamService = {
  /**
   * Get all pending team invitations for the current account
   */
  async getInvitations(): Promise<TeamInvitation[]> {
    const response = await api.get<{ invitations: TeamInvitation[] }>('/api/invitations');
    return response.data.invitations;
  },

  /**
   * Create a new team invitation
   * @param data Email and role for the invitation
   */
  async createInvitation(data: CreateInvitationData): Promise<InvitationResponse> {
    const response = await api.post<InvitationResponse>('/api/invitations', data);
    return response.data;
  },

  /**
   * Revoke a pending invitation
   * @param invitationId ID of the invitation to revoke
   */
  async revokeInvitation(invitationId: number): Promise<RevokeInvitationResponse> {
    const response = await api.delete<RevokeInvitationResponse>(`/api/invitations/${invitationId}`);
    return response.data;
  },

  /**
   * Accept a team invitation (used by invited user)
   * @param token Invitation token from email
   */
  async acceptInvitation(token: string): Promise<AcceptInvitationResponse> {
    const response = await api.post<AcceptInvitationResponse>(`/api/invitations/${token}/accept`);
    return response.data;
  },

  /**
   * Get all team members for the current account
   */
  async getMembers(): Promise<TeamMember[]> {
    const response = await api.get<{ members: TeamMember[] }>('/api/account/members');
    return response.data.members;
  },

  /**
   * Update a team member's role
   * @param memberId ID of the member to update
   * @param data New role data
   */
  async updateMemberRole(memberId: number, data: UpdateMemberRoleData): Promise<void> {
    await api.put(`/api/account/members/${memberId}`, data);
  },

  /**
   * Remove a team member from the account
   * @param memberId ID of the member to remove
   */
  async removeMember(memberId: number): Promise<void> {
    await api.delete(`/api/account/members/${memberId}`);
  },
};

export default teamService;
